import {
  ProductBuilderMachineContext,
  ProductBuilderMachineState,
  ProductBuilderMachineStateSchemaStates,
} from '../types';
import { getColourFromColourTag } from './colour';
import { getDriveControlFromDriveControlTag } from './driveControl';
import { getStates } from './getStates';

type GetBreakdown = (
  context: ProductBuilderMachineContext,
) => [ProductBuilderMachineState, string][];

type GetBreakdownEntry = (
  context: ProductBuilderMachineContext,
) => (entry: Entry) => [ProductBuilderMachineState, string];

type Value = string | number | [number, string];

type Entry = [ProductBuilderMachineState, Value];

type Entries = Entry[];

const getBreakdownEntry: GetBreakdownEntry = ({
  measureInsurance,
  powerSupply,
  remoteControl,
  extensionPole,
  smartRelease,
  wifiHub,
  fascia,
  lightGuards,
  venetianValance,
  stainlessSteelChain,
}) => ([key, value]) => {
  switch (key) {
    case 'measureInsurance': {
      if (typeof value === 'number') {
        const variant = measureInsurance.find(
          ({ first_available_variant: { id } }) => id === value,
        );
        const title = variant ? 'Yes' : '';
        return [key, title];
      }
      return [key, value ? value.toString() : ''];
    }
    case 'powerSupply': {
      if (Array.isArray(value)) {
        const [variantId, side] = value;
        const variant = powerSupply.find(
          ({ first_available_variant: { id } }) => id === variantId,
        );
        const title = variant ? variant.title : '';
        return [key, `${title}${side ? `, cable side ${side}` : ``}`];
      }
      return [key, value ? value.toString() : ''];
    }
    case 'remoteControl': {
      if (typeof value === 'number') {
        const variant = remoteControl.find(
          ({ first_available_variant: { id } }) => id === value,
        );
        const title = variant ? variant.title : '';
        return [key, title];
      }
      return [key, value ? value.toString() : ''];
    }
    case 'smartRelease': {
      if (typeof value === 'number') {
        const variant = smartRelease.find(
          ({ first_available_variant: { id } }) => id === value,
        );
        const title = variant ? variant.title : '';
        return [key, title];
      }
      return [key, value ? value.toString() : ''];
    }
    case 'extensionPole': {
      if (typeof value === 'number') {
        const variant = extensionPole.find(
          ({ first_available_variant: { id } }) => id === value,
        );
        const title = variant ? variant.title : '';
        return [key, title];
      }
      return [key, value ? value.toString() : ''];
    }
    // case 'stainlessSteelChain': {
    //   if (typeof value === 'number') {
    //     const variant = stainlessSteelChain.find(
    //       ({ first_available_variant: { id } }) => id === value,
    //     );
    //     const title = variant ? variant.title : '';
    //     return [key, title];
    //   }
    //   return [key, value ? value.toString() : ''];
    // }
    case 'wifiHub': {
      if (typeof value === 'number') {
        const variant = wifiHub.find(
          ({ first_available_variant: { id } }) => id === value,
        );
        const title = variant ? variant.title : '';
        return [key, title];
      }
      return [key, value ? value.toString() : ''];
    }
    case 'fascia': {
      if (typeof value === 'number') {
        const variant = fascia[0].variants.find(
          variant => variant.id === value,
        );
        const title = variant ? 'Yes' : '';
        return [key, title];
      }
      return [key, value ? value.toString() : ''];
    }
    case 'lightGuards': {
      if (typeof value === 'number') {
        const variant = lightGuards[0].variants.find(
          variant => variant.id === value,
        );
        const title = variant ? 'Yes' : '';
        return [key, title];
      }
      return [key, value ? value.toString() : ''];
    }
    case 'venetianValance': {
      if (typeof value === 'number') {
        const variant = venetianValance[0].variants.find(
          variant => variant.id === value,
        );
        const title = variant ? 'Yes' : '';
        return [key, title];
      }
      return [key, value ? value.toString() : ''];
    }
    case 'colour':
    case 'colourDay':
    case 'colourNight': {
      return [
        key,
        typeof value === 'string'
          ? getColourFromColourTag(value)
          : value.toString(),
      ];
    }
    case 'driveControl': {
      return [
        key,
        typeof value === 'string'
          ? getDriveControlFromDriveControlTag(value)
          : value.toString(),
      ];
    }
    case 'track': {
      return [key, typeof value === 'string' ? value.split(':')[0] : ''];
    }
    case 'trackColour': {
      return [key, value ? value.toString() : ''];
    }
    default: {
      return [key, value ? value.toString() : ''];
    }
  }
};

export const getBreakdown: GetBreakdown = context => {
  const { custom } = context;

  const states = getStates(context);
  const entriesAlt: Entries = states.map(stateKey => {
    return [stateKey, custom[stateKey] || ''] as Entry;
  });

  const breakdown = entriesAlt
    .filter(([key]) => key !== 'dynamicPrice' && key !== 'complete')
    .map(getBreakdownEntry(context));

  // Handle trackColour special case for curtain types
  const processedBreakdown = breakdown.map(([key, value]) => {
    if (key === 'trackColour') {
      // For curtain types, don't show trackColour in summary when trackRequire is "No"
      const isCurtain = context.products[0]?.type === "Curtain" || context.products[0]?.type === "Sheer Curtain";
      if (isCurtain && context.custom.trackRequire === 'No') {
        return [key, ''] as [keyof ProductBuilderMachineStateSchemaStates, string]; // Return empty string to hide from summary
      }
    }
    return [key, value];
  });

  return processedBreakdown as [keyof ProductBuilderMachineStateSchemaStates, string][];
};
